import { useCallback, useEffect, useState } from 'react';

import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import giftIcon from '../../../../assets/blue_gift.png';
import type { Promotion } from '../../../../dto/promo.types';
import { useCoinsAnimation } from '../../../../hooks/useCoinsAnimation';
import type { ClaimableReward } from '../../../../stores/ui/types';
import { useStores } from '../../../../stores/useStores';
import Button from '../../../buttons/Button';
import { DialogBox } from '../../../Dialog/DialogBox';
import { DialogContent } from '../../../Dialog/DialogContent';
import { ErrorMessage } from '../../../ErrorMessage/ErrorMessage';
import { PromoRewardsDisplay } from '../components/PromoRewardsDisplay';

import styles from './ClaimPromoGiftDialog.module.scss';

const ClaimPromoGiftDialog = observer(() => {
  const { walletStore, portalStore, promotionStore } = useStores();
  const { t } = useTranslation();

  const [errorMsg, setErrorMsg] = useState('');
  const [isClaiming, setIsClaiming] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const { startAnimation } = useCoinsAnimation();

  const { options } = portalStore;

  const promotion = options.promotion as Promotion;
  const promotionExists = !!promotion;

  const removePcQueryParam = useCallback(() => {
    if (searchParams.has('pc')) {
      searchParams.delete('pc');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams]);

  const closeDialog = useCallback(() => {
    removePcQueryParam();
    promotionStore.closePromotionDialog();
  }, [promotionStore, removePcQueryParam]);

  const onCloseClick = () => {
    promotionStore.sendAnalyticsEvent({ event_action: 'click', click_location: 'close' });
    closeDialog();
  };

  useEffect(() => {
    if (!promotionExists) {
      closeDialog();
    }
  }, [promotionExists, closeDialog]);

  const handleSubmit = async () => {
    if (errorMsg) {
      setErrorMsg('');
    }

    setIsClaiming(true);
    promotionStore.sendAnalyticsEvent({
      event_action: 'click',
      click_location: 'cta',
      button_text: t('common.buttons.claim'),
      action_result: 'claim_gift',
    });
    const updatedWalletData = await promotionStore.redeemPromoCode(promotion.promoCode);
    if (updatedWalletData) {
      const { gcCoin, scCoin, totalScCoin } = updatedWalletData;
      walletStore.updateWallet({ gcCoin, scCoin, totalScCoin });
      const rewards: ClaimableReward[] = [
        { scCoin: promotion.scAmount, gcCoin: promotion.gcAmount, bundleItems: promotion.bundleItems || [] },
      ];

      startAnimation({
        rewards,
        onEnd: () => closeDialog(),
      });
    } else {
      setErrorMsg('Failed to claim gift');
      setIsClaiming(false);
    }
  };

  return (
    <DialogBox>
      <div className={styles.header}>
        <button aria-label="Close" className={styles.closeButton} type="button" onClick={onCloseClick}>
          <svg
            aria-hidden="true"
            fill="none"
            height="24"
            viewBox="0 0 24 24"
            width="24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.5"
            />
          </svg>
        </button>
        <div className={styles.headerContent}>
          <div className={styles.giftIconWrapper}>
            <img alt="Gift" className={styles.giftIcon} src={giftIcon} />
          </div>
          {promotion.title && <h2 className={styles.title}>{promotion.title}</h2>}
          <p className={styles.subtitle}>{t('common.enjoyYourGift')}</p>
        </div>
      </div>

      <DialogContent className={styles.content}>
        <PromoRewardsDisplay
          bundleItems={promotion.bundleItems}
          className={styles.rewardsDisplay}
          gcAmount={promotion.gcAmount}
          scAmount={promotion.scAmount}
        />

        <Button
          className={styles.claimButton}
          dataTestId="claim-promo-gift-btn"
          disabled={isClaiming}
          onClick={handleSubmit}
        >
          {t('common.buttons.claim')}
        </Button>

        {errorMsg && <ErrorMessage className={styles.error}>{errorMsg}</ErrorMessage>}
      </DialogContent>
    </DialogBox>
  );
});

export default ClaimPromoGiftDialog;
